// AssignDevice.tsx (upraveno pro props)
import React, { useState } from 'react';
import { useAssignDeviceToUserMutation } from '../../state/api';
import { IconButton, Chip, Box } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import DeviceShareModal from './DeviceShareModal';
import './MyDevice.scss';

export interface AssignDeviceProps {
  userId: string;
  userDevices: any[];
  devicesLoading: boolean;
}

export default function AssignDevice({ userId, userDevices, devicesLoading }: AssignDeviceProps) {
  const [deviceId, setDeviceId] = useState('');
  const [expandedDevice, setExpandedDevice] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedDeviceForShare, setSelectedDeviceForShare] = useState<any>(null);
  const [assignDevice, { isLoading: assignLoading }] = useAssignDeviceToUserMutation();

  // Helper function to get device name for sorting
  const getDeviceName = (device: any): string => {
    return device.settings?.name || device.location || device._id;
  };

  // Sort devices alphabetically by name
  const sortedDevices = [...userDevices].sort((a, b) => {
    const nameA = getDeviceName(a).toLowerCase();
    const nameB = getDeviceName(b).toLowerCase();
    return nameA.localeCompare(nameB, 'cs');
  });

  const handleAssign = async () => {
    if (!deviceId) return;
    try {
      await assignDevice({ userId, deviceId }).unwrap();
      setDeviceId('');
    } catch (err) {
      console.error('Chyba při přiřazení zařízení:', err);
    }
  };

  const handleOpenShareModal = (device: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDeviceForShare(device);
    setShareModalOpen(true);
  };

  return (
    <div className="assign-device-container">
      <h2 className="assign-device-title">Přiřadit zařízení uživateli</h2>

      <div className="assign-device-input-wrapper">
        <input
          type="text"
          value={deviceId}
          onChange={(e) => setDeviceId(e.target.value)}
          placeholder="Zadej Device ID"
          className="assign-device-input"
        />
      </div>

      <button
        onClick={handleAssign}
        disabled={assignLoading || !deviceId}
        className="assign-device-button"
      >
        {assignLoading ? 'Přiřazuji...' : 'Přiřadit zařízení'}
      </button>

      <div className="user-devices-section">
        {devicesLoading ? (
          <p className="loading-text">Načítám zařízení...</p>
        ) : (
          <ul className="user-devices-list">
            {sortedDevices.length > 0 ? (
              sortedDevices.map((device: any) => {
                const isExpanded = expandedDevice === device._id;
                const isOwner = device.isOwner;
                const accessLevel = device.accessLevel;

                return (
                  <li
                    key={device._id}
                    className="device-card"
                    onClick={() => setExpandedDevice(isExpanded ? null : device._id)}
                  >
                    <div className="device-card-header">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <span className="device-name">
                          {device.settings?.name || device.location || device._id}
                        </span>
                        {!isOwner && (
                          <Chip
                            label={accessLevel === 'read' ? 'Jen čtení' : 'Sdílené'}
                            size="small"
                            color={accessLevel === 'read' ? 'default' : 'primary'}
                            sx={{ fontSize: '0.75rem' }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isOwner && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenShareModal(device, e)}
                            title="Sdílet zařízení"
                          >
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        )}
                        <span className="device-toggle">{isExpanded ? '▲' : '▼'}</span>
                      </Box>
                    </div>

                    {isExpanded && (
                      <div className="device-card-details">
                        <p>
                          <strong>ID:</strong> {device._id}
                        </p>
                        {device.location && (
                          <p>
                            <strong>Lokace:</strong> {device.location}
                          </p>
                        )}
                        {device.settings?.ipAddress && (
                          <p>
                            <strong>IP:</strong> {device.settings.ipAddress}
                          </p>
                        )}
                        {device.settings?.status && (
                          <p>
                            <strong>Status:</strong>{' '}
                            <span className={`status-badge status-${device.settings.status}`}>
                              {device.settings.status}
                            </span>
                          </p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })
            ) : (
              <li className="no-devices">Žádná zařízení</li>
            )}
          </ul>
        )}
      </div>

      {/* Share Modal */}
      {selectedDeviceForShare && (
        <DeviceShareModal
          open={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setSelectedDeviceForShare(null);
          }}
          deviceId={selectedDeviceForShare._id}
          deviceName={
            selectedDeviceForShare.settings?.name ||
            selectedDeviceForShare.location ||
            selectedDeviceForShare._id
          }
        />
      )}
    </div>
  );
}
